/**
 * The autonomy card — one surface, four zones:
 *
 *   1. Header: status dot + Active/Paused title + the Active toggle.
 *   2. Level: the LevelStepper and the capability ladder it drives.
 *   3. Where: coverage chips and the Customize disclosure…
 *   4. …whose panel is the per-scope TunePanel, opened in place.
 *
 * Level writes happen on commit, never during a drag: the stepper's radio
 * `onChange` updates a local draft, and keyboard steps within 400 ms collapse
 * into one write. The consumer owns the value — the card never moves the
 * selection itself.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  AccordionChevron,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion';
import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import type { LevelStep } from '@/lib/base/forms/level-stepper';
import { LevelStepper } from '@/lib/base/forms/level-stepper';
import { Toggle } from '@/lib/base/forms/toggle';
import { ListCard } from '@/lib/base/list-card';
import type { Pipeline } from '@/lib/crm/hooks/usePipelines';
import { cx } from '@/utils/cx';

import {
  ACTIVE_DESCRIPTION,
  ACTIVE_LABEL,
  CONTACTS_SCOPE_LABEL,
  CUSTOMIZE_LABEL,
  CUSTOMIZED_BADGE,
  LEVEL_EYEBROW,
  LEVEL_STEPPER_ARIA_LABEL,
  OFF_SUFFIX,
  PAUSED_DESCRIPTION_PREFIX,
  PAUSED_LABEL,
  RESET_LABEL_PREFIX,
  STOP_LABELS,
  WHERE_EYEBROW,
} from '../agent-autonomy-copy';
import type {
  AutonomyDerivation,
  AutonomyGrantState,
  AutonomyStop,
  CapabilityKey,
  ContactsVerb,
  PipelineVerb,
} from '../autonomyPresets';
import { applyLevel, AUTONOMY_STOPS, isAutonomyStop } from '../autonomyPresets';
import type { AgentCaseType } from '../hooks/useAgentSettings';
import { CapabilityLadder } from './CapabilityLadder';
import { TunePanel } from './TunePanel';

/** Keyboard steps within this window collapse into one write. */
const COMMIT_DEBOUNCE_MS = 400;
/** How long the pointer must rest on a stop before the ladder previews it. */
const PREVIEW_REST_MS = 140;

const STEPS: readonly LevelStep[] = AUTONOMY_STOPS.map(stop => ({
  value: String(stop),
  label: STOP_LABELS[stop],
}));

/** Zone eyebrows sit a tone above the ladder's group headers. */
const EYEBROW_CLASSES =
  'text-xs font-semibold uppercase tracking-wider text-fg-tertiary';

interface CoverageChipProps {
  label: string;
  on: boolean;
}

function CoverageChip({ label, on }: CoverageChipProps) {
  return (
    <Badge type='pill-color' size='sm' color={on ? 'brand' : 'gray'}>
      {on ? label : `${label} · ${OFF_SUFFIX}`}
    </Badge>
  );
}

export interface AutonomyCardProps {
  enabled: boolean;
  level: AutonomyStop;
  derived: AutonomyDerivation;
  grants: AutonomyGrantState;
  caseTypes: AgentCaseType[];
  pipelines: Pipeline[];
  isSaving: boolean;
  canWrite: boolean;
  onActive(on: boolean): void;
  onLevel(stop: AutonomyStop): void;
  onReset(): void;
  onCapability(key: CapabilityKey, on: boolean): void;
  onContactsScope(on: boolean): void;
  onPipelineScope(entityTypeName: string, on: boolean): void;
  onContactsVerb(verb: ContactsVerb, on: boolean): void;
  onPipelineVerb(entityTypeName: string, verb: PipelineVerb, on: boolean): void;
}

export function AutonomyCard({
  enabled,
  level,
  derived,
  grants,
  caseTypes,
  pipelines,
  isSaving,
  canWrite,
  onActive,
  onLevel,
  onReset,
  onCapability,
  onContactsScope,
  onPipelineScope,
  onContactsVerb,
  onPipelineVerb,
}: AutonomyCardProps) {
  const [draft, setDraft] = useState<AutonomyStop | null>(null);
  const [previewLevel, setPreviewLevel] = useState<AutonomyStop | null>(null);
  const commitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (commitTimer.current) {
        clearTimeout(commitTimer.current);
      }
      if (previewTimer.current) {
        clearTimeout(previewTimer.current);
      }
    },
    []
  );

  // Both the stepper and the ladder's stop marks set the level through here,
  // so a press from either gets the same debounce and the same write.
  const commitLevel = useCallback(
    (stop: AutonomyStop) => {
      setDraft(stop);
      if (commitTimer.current) {
        clearTimeout(commitTimer.current);
      }
      commitTimer.current = setTimeout(() => {
        commitTimer.current = null;
        setDraft(null);
        onLevel(stop);
      }, COMMIT_DEBOUNCE_MS);
    },
    [onLevel]
  );

  const handleStepperChange = useCallback(
    (next: string) => {
      const stop = Number(next);
      if (isAutonomyStop(stop)) {
        commitLevel(stop);
      }
    },
    [commitLevel]
  );

  // A preview shows only once the pointer RESTS on a stop: sweeping across the
  // stepper must not flash the ladder through every state on the way. Leaving
  // clears immediately.
  const previewStop = useCallback((stop: AutonomyStop | null) => {
    if (previewTimer.current) {
      clearTimeout(previewTimer.current);
      previewTimer.current = null;
    }
    if (stop === null) {
      setPreviewLevel(null);
      return;
    }
    previewTimer.current = setTimeout(() => {
      previewTimer.current = null;
      setPreviewLevel(stop);
    }, PREVIEW_REST_MS);
  }, []);

  const handleStepperPreview = useCallback(
    (next: string | null) => {
      if (next === null) {
        previewStop(null);
        return;
      }
      const stop = Number(next);
      if (isAutonomyStop(stop)) {
        previewStop(stop);
      }
    },
    [previewStop]
  );

  const shown = draft ?? level;
  const controlsDisabled = isSaving || !canWrite;
  const grantControlsDisabled = controlsDisabled || !enabled;
  // While a level press is still in its debounce window (`draft`), the queued
  // level write carries the pre-press grant snapshot. A ladder, tune, or Reset
  // change made in that window would be clobbered when the write lands, so
  // those controls are frozen until the draft commits. The stepper stays live
  // — its keyboard steps collapse into the same pending write.
  const grantEditsDisabled = grantControlsDisabled || draft !== null;
  const resetLabel = useMemo(
    () => `${RESET_LABEL_PREFIX} ${STOP_LABELS[level]}`,
    [level]
  );
  // The ladder describes a press as if it had landed — from the moment it is
  // pressed (draft) until the write's refetch arrives (`level` still ahead of
  // `derived.level`) — so it never snaps back to the old grants mid-save. A
  // hovered stop previews against that.
  const pendingStop = draft ?? (level !== derived.level ? level : null);
  const ladderGrants = useMemo(
    () =>
      pendingStop === null
        ? grants
        : applyLevel(grants, caseTypes, pendingStop),
    [pendingStop, grants, caseTypes]
  );
  // Hovering the selected stop normally previews nothing — the level isn't
  // changing. But when grants are customized, applying that stop WOULD reset
  // the per-scope exceptions, so the ladder should still ghost that reset.
  const previewGrants = useMemo(
    () =>
      previewLevel === null || (previewLevel === shown && !derived.customized)
        ? null
        : applyLevel(grants, caseTypes, previewLevel),
    [previewLevel, shown, derived.customized, grants, caseTypes]
  );

  return (
    <ListCard data-component='autonomy-card'>
      {/* 1. Header */}
      <div className='flex items-start gap-3 px-4 py-3'>
        <span
          aria-hidden='true'
          className={cx(
            'mt-1.5 size-2 shrink-0 rounded-full',
            enabled ? 'bg-utility-success-500' : 'bg-quaternary'
          )}
        />
        <div className='min-w-0 flex-1'>
          <p className='text-sm font-medium text-fg-primary'>
            {enabled ? ACTIVE_LABEL : PAUSED_LABEL}
          </p>
          <p className='text-sm text-fg-tertiary'>
            {enabled
              ? ACTIVE_DESCRIPTION
              : `${PAUSED_DESCRIPTION_PREFIX} ${STOP_LABELS[level]}.`}
          </p>
        </div>
        <Toggle
          isSelected={enabled}
          onChange={onActive}
          isDisabled={controlsDisabled}
          aria-label={ACTIVE_LABEL}
        />
      </div>

      {/* 2. Level */}
      <div
        className={cx(
          'px-4 pt-3 pb-4 transition-opacity',
          !enabled && 'opacity-60'
        )}
      >
        <div className='flex flex-wrap items-center justify-between gap-2'>
          <p className={EYEBROW_CLASSES}>{LEVEL_EYEBROW}</p>
          {derived.customized && (
            <div className='flex items-center gap-2'>
              <Badge type='pill-color' size='sm' color='gray'>
                {CUSTOMIZED_BADGE}
              </Badge>
              {!grantEditsDisabled && (
                <Button color='link-color' size='sm' onClick={onReset}>
                  {resetLabel}
                </Button>
              )}
            </div>
          )}
        </div>
        <div className='mt-3'>
          <LevelStepper
            aria-label={LEVEL_STEPPER_ARIA_LABEL}
            steps={STEPS}
            value={String(shown)}
            previewValue={previewGrants === null ? null : String(previewLevel)}
            onChange={handleStepperChange}
            onPreview={handleStepperPreview}
            isDisabled={grantControlsDisabled}
          />
        </div>
        <div className='mt-3'>
          <CapabilityLadder
            grants={ladderGrants}
            previewGrants={previewGrants}
            caseTypes={caseTypes}
            isDisabled={grantEditsDisabled}
            onLevel={commitLevel}
            onCapability={onCapability}
          />
        </div>
      </div>

      {/* 3 + 4. Where, with Customize opening the tune panel in place */}
      <AccordionItem>
        <div
          className={cx(
            'flex flex-wrap items-center gap-2 px-4 py-3 transition-opacity',
            !enabled && 'opacity-60'
          )}
        >
          <p className={cx(EYEBROW_CLASSES, 'mr-1')}>{WHERE_EYEBROW}</p>
          <CoverageChip
            label={CONTACTS_SCOPE_LABEL}
            on={derived.contactsGranted}
          />
          {caseTypes.map(caseType => (
            <CoverageChip
              key={caseType.entityTypeName}
              label={caseType.label}
              on={caseType.entityTypeName in grants.pipelineGrants}
            />
          ))}
          {/* At phone width the chips wrap to several lines; the trigger takes
              its own full line, right-aligned, instead of stranding itself
              beside the last chip. */}
          <AccordionTrigger className='flex items-center gap-1 text-sm font-medium text-fg-brand-secondary hover:text-fg-brand-primary max-sm:w-full max-sm:justify-end sm:ml-auto'>
            <span>{CUSTOMIZE_LABEL}</span>
            <AccordionChevron variant='swap' className='size-4' />
          </AccordionTrigger>
        </div>
        <AccordionPanel className='border-t border-border-secondary bg-secondary px-4 py-3'>
          <TunePanel
            grants={grants}
            derived={derived}
            caseTypes={caseTypes}
            pipelines={pipelines}
            isDisabled={grantEditsDisabled}
            onContactsScope={onContactsScope}
            onPipelineScope={onPipelineScope}
            onContactsVerb={onContactsVerb}
            onPipelineVerb={onPipelineVerb}
          />
        </AccordionPanel>
      </AccordionItem>
    </ListCard>
  );
}
